import { useState, useEffect, useCallback } from 'react'
import { Row, Col, Card, Button, Modal, Form, Input, Select, DatePicker, Badge, Spin, message, Tooltip } from 'antd'
import {
    LeftOutlined,
    RightOutlined,
    PlusOutlined,
    CalendarOutlined
} from '@ant-design/icons'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import 'dayjs/locale/ru'
import api from '../services/api'
import { useAuthStore } from '../store/authStore'

dayjs.extend(utc)
dayjs.extend(timezone)
dayjs.locale('ru')

const WEEKDAYS = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс']
const MONTHS = ['Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь', 'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь']

const priorityColors: Record<string, string> = {
    low: '#10b981',
    medium: '#f59e0b',
    high: '#f97316',
    urgent: '#ef4444'
}

const priorityLabels: Record<string, string> = {
    low: 'Низкий',
    medium: 'Средний',
    high: 'Высокий',
    urgent: 'Срочно'
}

interface Task {
    id: number
    title: string
    status: string
    priority: string
    deadline: string
}

interface CalendarData {
    [day: number]: Task[]
}

// Analog Clock Component
const AnalogClock = ({ time }: { time: dayjs.Dayjs }) => {
    const hours = time.hour() % 12
    const minutes = time.minute()
    const seconds = time.second()

    const hourDeg = (hours * 30) + (minutes * 0.5)
    const minuteDeg = minutes * 6
    const secondDeg = seconds * 6

    return (
        <div style={{
            width: 100,
            height: 100,
            borderRadius: '50%',
            background: 'var(--bg-surface)',
            border: '2px solid var(--border)',
            position: 'relative',
            boxShadow: 'var(--shadow-lg)'
        }}>
            {/* Clock face markers */}
            {[...Array(12)].map((_, i) => (
                <div
                    key={i}
                    style={{
                        position: 'absolute',
                        width: i % 3 === 0 ? 3 : 2,
                        height: i % 3 === 0 ? 10 : 6,
                        background: 'var(--text-muted)',
                        borderRadius: 2,
                        top: 4,
                        left: '50%',
                        transform: `translateX(-50%) rotate(${i * 30}deg)`,
                        transformOrigin: '50% 46px'
                    }}
                />
            ))}

            {/* Hour hand */}
            <div style={{
                position: 'absolute',
                width: 4,
                height: 28,
                background: 'var(--text-primary)',
                borderRadius: 4,
                top: '50%',
                left: '50%',
                transformOrigin: '50% 100%',
                transform: `translate(-50%, -100%) rotate(${hourDeg}deg)`,
                boxShadow: '0 2px 4px rgba(0,0,0,0.3)',
                transition: 'transform 0.5s cubic-bezier(0.4, 2.08, 0.55, 0.44)'
            }} />

            {/* Minute hand */}
            <div style={{
                position: 'absolute',
                width: 3,
                height: 38,
                background: 'var(--text-primary)',
                borderRadius: 3,
                top: '50%',
                left: '50%',
                transformOrigin: '50% 100%',
                transform: `translate(-50%, -100%) rotate(${minuteDeg}deg)`,
                boxShadow: '0 2px 4px rgba(0,0,0,0.3)',
                transition: 'transform 0.3s ease-out'
            }} />

            {/* Second hand */}
            <div style={{
                position: 'absolute',
                width: 2,
                height: 42,
                background: '#ef4444',
                borderRadius: 2,
                top: '50%',
                left: '50%',
                transformOrigin: '50% 100%',
                transform: `translate(-50%, -100%) rotate(${secondDeg}deg)`,
                boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
            }} />

            {/* Center dot */}
            <div style={{
                position: 'absolute',
                width: 8,
                height: 8,
                background: '#ef4444',
                borderRadius: '50%',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                boxShadow: '0 0 4px rgba(0,0,0,0.3)'
            }} />

            {/* Outer ring animation */}
            <div style={{
                position: 'absolute',
                inset: -3,
                borderRadius: '50%',
                border: '2px solid var(--border)',
                animation: 'pulse-ring 2s ease-in-out infinite'
            }} />
        </div>
    )
}

export default function Calendar() {
    const [currentDate, setCurrentDate] = useState(dayjs())
    const [moscowTime, setMoscowTime] = useState(dayjs().tz('Europe/Moscow'))
    const [calendarData, setCalendarData] = useState<CalendarData>({})
    const [loading, setLoading] = useState(false)
    const [modalVisible, setModalVisible] = useState(false)
    const [selectedDay, setSelectedDay] = useState<number | null>(null)
    const [form] = Form.useForm()
    const [operators, setOperators] = useState<any[]>([])
    const { user } = useAuthStore()

    // Update Moscow time every second
    useEffect(() => {
        const timer = setInterval(() => {
            setMoscowTime(dayjs().tz('Europe/Moscow'))
        }, 1000)
        return () => clearInterval(timer)
    }, [])

    // Load calendar data
    const loadCalendarData = useCallback(async () => {
        setLoading(true)
        try {
            const response = await api.get(`/tasks/calendar/${currentDate.year()}/${currentDate.month() + 1}`)
            setCalendarData(response.data)
        } catch (error) {
            console.error('Failed to load calendar data:', error)
        } finally {
            setLoading(false)
        }
    }, [currentDate])

    // Load operators for assignment
    const loadOperators = async () => {
        try {
            const response = await api.get('/operators')
            setOperators(response.data)
        } catch (error) {
            console.error('Failed to load operators:', error)
        }
    }

    useEffect(() => {
        loadCalendarData()
        loadOperators()
    }, [loadCalendarData])

    const prevMonth = () => setCurrentDate(currentDate.subtract(1, 'month'))
    const nextMonth = () => setCurrentDate(currentDate.add(1, 'month'))
    const goToToday = () => setCurrentDate(dayjs())

    const getDaysInMonth = () => {
        const startOfMonth = currentDate.startOf('month')
        const endOfMonth = currentDate.endOf('month')
        const startDay = startOfMonth.day() === 0 ? 6 : startOfMonth.day() - 1
        const daysInMonth = endOfMonth.date()

        const days = []

        // Previous month days
        const prevMonthDate = currentDate.subtract(1, 'month')
        const prevMonthDays = prevMonthDate.daysInMonth()
        for (let i = startDay - 1; i >= 0; i--) {
            days.push({ day: prevMonthDays - i, isCurrentMonth: false })
        }

        // Current month days
        for (let i = 1; i <= daysInMonth; i++) {
            days.push({ day: i, isCurrentMonth: true })
        }

        // Next month days - only fill to 35 for compact view
        const remaining = 35 - days.length
        for (let i = 1; i <= Math.max(0, remaining); i++) {
            days.push({ day: i, isCurrentMonth: false })
        }

        return days
    }

    const handleDayClick = (day: number, isCurrentMonth: boolean) => {
        if (!isCurrentMonth) return
        setSelectedDay(day)
        form.setFieldsValue({
            deadline: dayjs(currentDate.format('YYYY-MM') + '-' + String(day).padStart(2, '0'))
        })
        setModalVisible(true)
    }

    const handleCreateTask = async (values: any) => {
        try {
            await api.post('/tasks', {
                ...values,
                deadline: values.deadline?.toISOString(),
                reminder_at: values.reminder_at?.toISOString()
            })
            message.success('Задача создана')
            setModalVisible(false)
            form.resetFields()
            loadCalendarData()
        } catch (error) {
            message.error('Ошибка создания задачи')
        }
    }

    const isToday = (day: number, isCurrentMonth: boolean) => {
        return isCurrentMonth &&
            day === dayjs().date() &&
            currentDate.month() === dayjs().month() &&
            currentDate.year() === dayjs().year()
    }

    return (
        <div style={{ padding: 24 }}>
            {/* Moscow Time Widget - Compact */}
            <Card
                style={{
                    background: 'var(--bg-surface)',
                    borderRadius: 16,
                    marginBottom: 20,
                    border: '1px solid var(--border)',
                }}
                bodyStyle={{ padding: '16px 24px' }}
            >
                <Row align="middle" justify="space-between">
                    <Col>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 24 }}>
                            <AnalogClock time={moscowTime} />
                            <div>
                                <div style={{ color: 'var(--text-secondary)', fontSize: 12, marginBottom: 2 }}>
                                    Московское время (МСК)
                                </div>
                                <div style={{
                                    color: 'var(--text-primary)',
                                    fontSize: 42,
                                    fontWeight: 700,
                                    fontFamily: 'monospace',
                                    letterSpacing: 3,
                                    lineHeight: 1
                                }}>
                                    {moscowTime.format('HH:mm:ss')}
                                </div>
                                <div style={{ color: 'var(--text-secondary)', fontSize: 14, marginTop: 4 }}>
                                    {moscowTime.format('dddd, D MMMM YYYY')}
                                </div>
                            </div>
                        </div>
                    </Col>
                    <Col>
                        <div style={{ display: 'flex', gap: 8 }}>
                            <Button
                                onClick={goToToday}
                                icon={<CalendarOutlined />}
                                style={{ background: 'rgba(255,255,255,0.2)', border: 'none', color: 'white' }}
                            >
                                Сегодня
                            </Button>
                            <Button
                                type="primary"
                                icon={<PlusOutlined />}
                                onClick={() => {
                                    setSelectedDay(null)
                                    setModalVisible(true)
                                }}
                                style={{
                                    background: 'rgba(255,255,255,0.25)',
                                    border: 'none'
                                }}
                            >
                                Задача
                            </Button>
                        </div>
                    </Col>
                </Row>
            </Card>

            {/* Calendar - Compact */}
            <Card
                style={{
                    background: 'var(--bg-surface)',
                    borderRadius: 16,
                    border: '1px solid var(--border)',
                }}
                bodyStyle={{ padding: 0 }}
            >
                {/* Calendar Header - Compact */}
                <div style={{
                    padding: '12px 16px',
                    borderBottom: '1px solid var(--border)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 12
                }}>
                    <Button
                        type="text"
                        icon={<LeftOutlined />}
                        onClick={prevMonth}
                        size="small"
                        style={{ color: 'var(--text-primary)' }}
                    />
                    <div style={{
                        fontSize: 18,
                        fontWeight: 700,
                        color: 'var(--text-primary)',
                        minWidth: 200,
                        textAlign: 'center'
                    }}>
                        {MONTHS[currentDate.month()]} {currentDate.year()}
                    </div>
                    <Button
                        type="text"
                        icon={<RightOutlined />}
                        onClick={nextMonth}
                        size="small"
                        style={{ color: 'var(--text-primary)' }}
                    />
                </div>

                {/* Weekday Headers - Compact */}
                <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(7, 1fr)',
                    borderBottom: '1px solid var(--border)',
                }}>
                    {WEEKDAYS.map((day, idx) => (
                        <div
                            key={day}
                            style={{
                                padding: '8px 0',
                                textAlign: 'center',
                                fontWeight: 600,
                                color: idx >= 5 ? '#ef4444' : 'var(--text-muted)',
                                fontSize: 11,
                                textTransform: 'uppercase'
                            }}
                        >
                            {day}
                        </div>
                    ))}
                </div>

                {/* Calendar Grid - Compact */}
                <Spin spinning={loading}>
                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(7, 1fr)',
                    }}>
                        {getDaysInMonth().map((item, idx) => {
                            const dayTasks = item.isCurrentMonth ? (calendarData[item.day] || []) : []
                            const today = isToday(item.day, item.isCurrentMonth)

                            return (
                                <div
                                    key={idx}
                                    onClick={() => handleDayClick(item.day, item.isCurrentMonth)}
                                    style={{
                                        height: 70,
                                        padding: 4,
                                        borderRight: (idx + 1) % 7 !== 0 ? '1px solid var(--border)' : 'none',
                                        borderBottom: idx < 28 ? '1px solid var(--border)' : 'none',
                                        cursor: item.isCurrentMonth ? 'pointer' : 'default',
                                        background: today
                                            ? 'rgba(102, 126, 234, 0.1)'
                                            : item.isCurrentMonth
                                                ? 'transparent'
                                                : 'var(--bg-hover)',
                                        transition: 'background 0.2s',
                                    }}
                                    onMouseEnter={(e) => {
                                        if (item.isCurrentMonth && !today) {
                                            e.currentTarget.style.background = 'var(--bg-hover)'
                                        }
                                    }}
                                    onMouseLeave={(e) => {
                                        if (item.isCurrentMonth && !today) {
                                            e.currentTarget.style.background = 'transparent'
                                        }
                                    }}
                                >
                                    <div style={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        marginBottom: 2
                                    }}>
                                        <span style={{
                                            width: 22,
                                            height: 22,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            borderRadius: '50%',
                                            background: today ? 'var(--brand-primary)' : 'transparent',
                                            color: today
                                                ? 'white'
                                                : item.isCurrentMonth
                                                    ? 'var(--text-primary)'
                                                    : 'var(--text-muted)',
                                            fontWeight: today ? 700 : 500,
                                            fontSize: 12
                                        }}>
                                            {item.day}
                                        </span>
                                        {dayTasks.length > 0 && (
                                            <Badge
                                                count={dayTasks.length}
                                                style={{
                                                    backgroundColor: '#667eea',
                                                    fontSize: 9,
                                                    height: 16,
                                                    minWidth: 16,
                                                    lineHeight: '16px'
                                                }}
                                            />
                                        )}
                                    </div>

                                    {/* Task Pills - Compact */}
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                        {dayTasks.slice(0, 2).map(task => (
                                            <Tooltip key={task.id} title={task.title}>
                                                <div style={{
                                                    padding: '1px 4px',
                                                    borderRadius: 3,
                                                    background: priorityColors[task.priority] + '20',
                                                    borderLeft: `2px solid ${priorityColors[task.priority]}`,
                                                    fontSize: 9,
                                                    color: 'var(--text-primary)',
                                                    whiteSpace: 'nowrap',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    opacity: task.status === 'completed' ? 0.5 : 1,
                                                    textDecoration: task.status === 'completed' ? 'line-through' : 'none'
                                                }}>
                                                    {task.title}
                                                </div>
                                            </Tooltip>
                                        ))}
                                        {dayTasks.length > 2 && (
                                            <div style={{
                                                fontSize: 8,
                                                color: 'var(--text-muted)',
                                                paddingLeft: 4
                                            }}>
                                                +{dayTasks.length - 2}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )
                        })}
                    </div>
                </Spin>
            </Card>

            {/* CSS for clock animation */}
            <style>{`
                @keyframes pulse-ring {
                    0%, 100% { opacity: 0.5; transform: scale(1); }
                    50% { opacity: 1; transform: scale(1.05); }
                }
            `}</style>

            {/* Create Task Modal */}
            <Modal
                title={
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <PlusOutlined style={{ color: '#667eea' }} />
                        <span>Новая задача {selectedDay ? `на ${selectedDay} ${MONTHS[currentDate.month()]}` : ''}</span>
                    </div>
                }
                open={modalVisible}
                onCancel={() => {
                    setModalVisible(false)
                    form.resetFields()
                }}
                footer={null}
                width={500}
            >
                <Form
                    form={form}
                    layout="vertical"
                    onFinish={handleCreateTask}
                    initialValues={{
                        priority: 'medium',
                        status: 'pending',
                        assigned_to_id: user?.id
                    }}
                    style={{ marginTop: 16 }}
                >
                    <Form.Item
                        name="title"
                        label="Название"
                        rules={[{ required: true, message: 'Введите название' }]}
                    >
                        <Input placeholder="Что нужно сделать?" size="large" />
                    </Form.Item>

                    <Form.Item name="description" label="Описание">
                        <Input.TextArea rows={2} placeholder="Детали задачи..." />
                    </Form.Item>

                    <Row gutter={12}>
                        <Col span={12}>
                            <Form.Item name="priority" label="Приоритет">
                                <Select size="large">
                                    {Object.entries(priorityLabels).map(([key, label]) => (
                                        <Select.Option key={key} value={key}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                                <div style={{
                                                    width: 8,
                                                    height: 8,
                                                    borderRadius: '50%',
                                                    background: priorityColors[key]
                                                }} />
                                                {label}
                                            </div>
                                        </Select.Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item name="deadline" label="Дедлайн">
                                <DatePicker
                                    showTime
                                    format="DD.MM.YYYY HH:mm"
                                    style={{ width: '100%' }}
                                    size="large"
                                />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Form.Item name="assigned_to_id" label="Исполнитель">
                        <Select size="large" placeholder="Выберите исполнителя">
                            {operators.map((op: any) => (
                                <Select.Option key={op.id} value={op.id}>
                                    {op.full_name || op.username}
                                </Select.Option>
                            ))}
                        </Select>
                    </Form.Item>

                    <Form.Item style={{ marginBottom: 0, marginTop: 20 }}>
                        <Button
                            type="primary"
                            htmlType="submit"
                            block
                            size="large"
                            style={{
                                background: 'var(--brand-primary)',
                                border: 'none',
                                height: 46,
                                fontWeight: 600
                            }}
                        >
                            Создать задачу
                        </Button>
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    )
}
